import {
  DndContext, DragOverlay, useSensor, useSensors, PointerSensor, KeyboardSensor,
  type DragEndEvent, type Announcements,
} from "@dnd-kit/core";
import { useCallback, useMemo, useState } from "react";
import { useAuth } from "../../contexts/auth-context";
import type { Lead, LeadStage } from "../../lib/leads";
import { LEAD_STAGES, STAGE_LABELS, stageOf, toLeadSummary } from "../../lib/leads";
import { updateLeadStage } from "../../lib/api";
import { StatusMessage } from "../StatusMessage";
import { KanbanColumn } from "./KanbanColumn";
import { LeadCard } from "./LeadCard";

interface KanbanBoardProps {
  leads: Lead[];
  onLeadUpdated: (lead: Lead) => void;
}

export function KanbanBoard({ leads, onLeadUpdated }: KanbanBoardProps) {
  const { user } = useAuth();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // AUD-015 (blocker): PointerSensor alone left the ONLY way to change a
  // lead's stage entirely unreachable by keyboard — dnd-kit's useDraggable
  // still made each card LOOK operable to assistive tech (role="button",
  // tabindex="0", aria-roledescription="draggable"), but with no
  // KeyboardSensor registered, Space/Enter on a focused card did nothing at
  // all. KeyboardSensor ships dnd-kit's own default keyboard support (Tab to
  // a card, Space/Enter to pick up, arrow keys to move, Space/Enter to drop,
  // Escape to cancel) — no SortableContext needed, it works against any
  // DndContext by moving a virtual position and running the same collision
  // detection PointerSensor uses. See KanbanBoard.keyboard.test.tsx, which
  // drives a full keyboard pick-up/move/drop sequence (with mocked column
  // geometry, since jsdom has no real layout) to prove this actually lands
  // on a column, rather than assuming the default coordinate getter works
  // with this grid layout.
  const sensors = useSensors(
    // Small drag threshold so a click on the "View" link isn't read as a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // Group leads by stage (legacy leads without a stage land in "new").
  const leadsByStage: Record<LeadStage, Lead[]> = {
    new: [], contacted: [], quoted: [], won: [], lost: [],
  };
  for (const lead of leads) {
    leadsByStage[stageOf(lead)].push(lead);
  }

  // Shared by both the drag path (handleDragEnd below) AND the non-drag
  // "Move to…" per-card <select> fallback (AUD-015's second half — keyboard
  // -drag alone was judged too fragile a single point of failure for this
  // workflow) — same optimistic-update-then-rollback, same error handling.
  const moveLeadToStage = useCallback(async (idempotencyKey: string, targetStage: LeadStage) => {
    if (!user) return;
    const lead = leads.find((l) => l.idempotencyKey === idempotencyKey);
    if (!lead) return;
    if (stageOf(lead) === targetStage) return;

    // Optimistic update — won/lost also close locally to mirror the server,
    // so the card reflects the terminal state immediately.
    const closes = targetStage === "won" || targetStage === "lost";
    onLeadUpdated({
      ...lead,
      stage: targetStage,
      ...(closes ? { submissionStatus: "closed" as const } : {}),
    });

    try {
      const updated = await updateLeadStage(user.idToken, idempotencyKey, targetStage);
      onLeadUpdated(toLeadSummary(updated));
    } catch (err) {
      onLeadUpdated(lead); // rollback to the pre-move lead
      // AUD-011: drop the raw `err.message` suffix this used to glue onto
      // the friendly prefix ("Could not move lead: Internal server error").
      // The prefix alone is already sufficient friendly copy per the
      // audit's own fix direction; the raw error still goes to the console.
      console.error("[KanbanBoard] failed to move lead:", err);
      setError("Could not move lead — try again.");
    }
  }, [user, leads, onLeadUpdated]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    void moveLeadToStage(active.id as string, over.id as LeadStage);
  }, [moveLeadToStage]);

  const activeLead = activeId
    ? leads.find((l) => l.idempotencyKey === activeId) ?? null
    : null;

  // AUD-015: "verify the announcements... work with the column layout,
  // don't assume" — dnd-kit's OWN defaults announce raw ids ("Draggable
  // item 3f2a-... was moved over droppable area quoted"), which is
  // technically informative but reads a raw UUID aloud. These use the
  // homeowner's name and the column's display label instead.
  const accessibility = useMemo(() => {
    const leadLabel = (id: string) =>
      leads.find(l => l.idempotencyKey === id)?.homeownerName ?? "the lead";
    const announcements: Announcements = {
      onDragStart({ active }) {
        return `Picked up ${leadLabel(active.id as string)}.`;
      },
      onDragOver({ active, over }) {
        if (!over) return `${leadLabel(active.id as string)} is no longer over a stage column.`;
        return `${leadLabel(active.id as string)} is over the ${STAGE_LABELS[over.id as LeadStage]} column.`;
      },
      onDragEnd({ active, over }) {
        if (!over) return `${leadLabel(active.id as string)} was dropped without changing stage.`;
        return `${leadLabel(active.id as string)} was moved to the ${STAGE_LABELS[over.id as LeadStage]} stage.`;
      },
      onDragCancel({ active }) {
        return `Moving ${leadLabel(active.id as string)} was cancelled.`;
      },
    };
    return { announcements };
  }, [leads]);

  return (
    <div>
      {error && (
        <StatusMessage variant="error" message={error} className="error-banner">
          <button onClick={() => setError(null)}>Dismiss</button>
        </StatusMessage>
      )}
      <DndContext
        sensors={sensors}
        accessibility={accessibility}
        onDragStart={(e) => setActiveId(e.active.id as string)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="kanban-board">
          {LEAD_STAGES.map((stage) => (
            <KanbanColumn
              key={stage}
              stage={stage}
              title={STAGE_LABELS[stage]}
              leads={leadsByStage[stage]}
              onMoveToStage={moveLeadToStage}
            />
          ))}
        </div>
        <DragOverlay>
          {activeLead && <LeadCard lead={activeLead} dragging />}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
